/**
 * Agent creates a deal ON BEHALF of a rostered athlete (D9).
 *
 * DELIBERATELY NOT an extraction of `deals/new.tsx`. That form is 641
 * lines on the athlete's daily-use path and contains the AI extraction
 * flow, which cannot currently be exercised (the Anthropic account is at
 * its spend cap). Refactoring code that can't be tested, on the hottest
 * path in the app, to save some duplication is a bad trade — see risk #5
 * in the epic plan. This route composes the same building blocks
 * (CompensationEditor, PayorTypeahead) as its own screen instead.
 *
 * It's also legitimately a different form: the agent negotiated these
 * terms and has them in hand. Parsing a DM screenshot is an athlete
 * affordance, so the AI entry points are intentionally absent here.
 *
 * Everything created here lands as a DRAFT the athlete confirms — the
 * server forces that regardless of what this form sends.
 */
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  toast,
} from '@app/ui';
import type { CompensationItemInput } from '@app/validators';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { CompensationEditor } from '../../../../../../components/compensation-editor';
import { type PayorOption, PayorTypeahead } from '../../../../../../components/payor-typeahead';
import { api } from '../../../../../../lib/api';

export const Route = createFileRoute('/_authenticated/agency/athletes/$athleteId/deals/new')({
  loader: async ({ context, params }) => {
    const { getServerApi } = await import('../../../../../../lib/api-server');
    const serverApi = await getServerApi();
    const { agencies } = await context.queryClient.ensureQueryData(
      serverApi.agency.myAgencies.queryOptions(),
    );
    const orgId = agencies[0]?.id;
    if (orgId) {
      await context.queryClient.ensureQueryData(
        serverApi.agency.getAthlete.queryOptions({
          input: { orgId, athleteId: params.athleteId },
        }),
      );
    }
  },
  component: AgencyNewDealPage,
});

function AgencyNewDealPage() {
  const { athleteId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: mine } = useSuspenseQuery(api.agency.myAgencies.queryOptions());
  const orgId = mine.agencies[0].id;
  const { data: athleteData } = useSuspenseQuery(
    api.agency.getAthlete.queryOptions({ input: { orgId, athleteId } }),
  );

  const [title, setTitle] = useState('');
  const [brandName, setBrandName] = useState('');
  const [selectedPayor, setSelectedPayor] = useState<PayorOption | undefined>(undefined);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [commission, setCommission] = useState(
    athleteData.memberCommissionPercent === null
      ? ''
      : String(athleteData.memberCommissionPercent),
  );
  const [items, setItems] = useState<CompensationItemInput[]>([
    { type: 'CASH', amount: 0, currency: 'USD', description: 'Cash payment', orderIndex: 0 },
  ]);

  const createMutation = useMutation({
    ...api.agency.createDealFor.mutationOptions(),
    onSuccess: () => {
      toast.success(
        `Deal sent to ${athleteData.athlete.name || 'the athlete'} to review and confirm`,
      );
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey?.[0]) && q.queryKey[0][0] === 'agency',
      });
      navigate({ to: '/agency/athletes/$athleteId', params: { athleteId } });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not create the deal'),
  });

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error('Give the deal a title');
      return;
    }
    const resolvedBrand = selectedPayor?.legalName ?? brandName.trim();
    if (!resolvedBrand) {
      toast.error('Pick a payor or enter a brand name');
      return;
    }
    if (items.length === 0) {
      toast.error('Add at least one compensation item');
      return;
    }
    if (items.some((it) => !it.description?.trim())) {
      toast.error('Each compensation item needs a short description');
      return;
    }

    const total = items.reduce((s, it) => s + Number(it.amount || 0), 0);
    const trimmedCommission = commission.trim();
    let commissionPercent: number | null = null;
    if (trimmedCommission !== '') {
      const n = Number(trimmedCommission);
      if (Number.isNaN(n) || n < 0 || n > 100) {
        toast.error('Commission must be between 0 and 100');
        return;
      }
      commissionPercent = n;
    }

    createMutation.mutate({
      orgId,
      athleteId,
      commissionPercent,
      deal: {
        title: title.trim(),
        brandName: resolvedBrand,
        payorId: selectedPayor?.id,
        amount: total,
        currency: 'USD',
        // Server forces DRAFT regardless; sent for clarity.
        status: 'DRAFT',
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        notes: notes.trim() || undefined,
        isExclusive: false,
        source: 'agent',
        compensationItems: items,
      },
    });
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <Link
          to="/agency/athletes/$athleteId"
          params={{ athleteId }}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to {athleteData.athlete.name || 'athlete'}
        </Link>
        <h1 className="text-2xl font-bold mt-2">
          Add a deal for {athleteData.athlete.name || athleteData.athlete.email}
        </h1>
      </div>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="py-3 text-sm text-blue-900">
          This will be saved as a <strong>draft</strong> and sent to the athlete to review and
          confirm. It won't count as an active deal until they do.
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deal details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Summer campaign — 3 IG posts"
            />
          </div>

          <div>
            <Label>Who's paying?</Label>
            <PayorTypeahead
              value={selectedPayor}
              onSelect={(p) => {
                setSelectedPayor(p);
                if (p) setBrandName(p.legalName);
              }}
              onRequestCreate={(initial) =>
                // Agents create payors on the Payors screen (which is why
                // that nav link stays visible for them). Send them there
                // with the typed name prefilled and a way back here.
                navigate({
                  to: '/payors/$payorId',
                  params: { payorId: 'new' },
                  search: { name: initial, returnTo: `/agency/athletes/${athleteId}/deals/new` },
                })
              }
              placeholder="Type a brand or business name"
            />
            {!selectedPayor && (
              <div className="mt-2">
                <Label htmlFor="brand">Or enter a brand name</Label>
                <Input
                  id="brand"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g. Nike"
                />
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start">Start date</Label>
              <Input
                id="start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end">End date</Label>
              <Input
                id="end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Compensation</CardTitle>
        </CardHeader>
        <CardContent>
          <CompensationEditor items={items} onChange={setItems} currency="USD" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your commission</CardTitle>
          <CardDescription>
            Defaults to your standing rate for this athlete. Override it here for this deal only.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-w-xs">
            <Label htmlFor="commission">Percent</Label>
            <Input
              id="commission"
              inputMode="decimal"
              value={commission}
              onChange={(e) => setCommission(e.target.value)}
              placeholder="e.g. 15"
            />
            <p className="text-xs text-muted-foreground mt-1">Leave blank for no commission.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Notes (optional)</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything the athlete should know"
          />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Link
          to="/agency/athletes/$athleteId"
          params={{ athleteId }}
          className="inline-flex items-center rounded-md border px-4 py-2 text-sm hover:bg-muted"
        >
          Cancel
        </Link>
        <Button onClick={handleSubmit} disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Sending…' : 'Send to athlete'}
        </Button>
      </div>
    </div>
  );
}
